import { store } from '@/app/store'
import { pipelineService } from '@/features/adp/services/pipeline/pipeline.service'
import { syncCanvasState } from '@/features/adp/state/canvas/canvasSlice'
import { openOverlay } from '@/features/adp/state/overlaySlice'
import { setPipelineState } from '@/features/adp/state/pipelineSlice'
import { useMutation } from '@tanstack/react-query'
import { mergeAdpSessionNode } from './useAdpSessionStore'
import { useSourceData } from './useSourceData'

/**
 * Inputs for finalizing the registered-source flow (Table List Save or
 * Add New Table Save). `sourceFiles` is passed straight through to
 * `setSourceData` and is the shape `DataProfilingConfirmation` reads.
 */
export type FinalizeRegisteredSourceParams = {
  nodeId: string
  sourceDataSourceId?: number | null
  /** `roo_id` of the registered source; required by the data-flow mapping payload at column-mapping save. */
  rooId?: number | null
  sourceName?: string | null
  tableName: string
  sourceFiles: unknown
  /** Persisted as `sourceConfigurationState.values` for parity with new-source nodes. */
  registeredSourceValues?: Record<string, string>
  /**
   * Database connection block persisted as `sourceConfigurationState.submitted_source_payload`
   * so reopening the Explorer dialog (gear icon → "configuration-details" completed)
   * can build a valid `/connections/columns` request when the user picks a different
   * table. Without it `ExplorerDialog` sees `columnsPayload === null` and the columns
   * skeleton never resolves.
   */
  submittedSourcePayload?: Record<string, unknown> | null
}

type FinalizeRegisteredSourceResult = {
  sessionId: string
  dataFlowId: number
}

/**
 * Wraps the post-save side effects for the registered-source flow:
 *   - Create the data flow on first save (so DQ profiling has a session id)
 *   - Persist `source_files` / `source_selected_table` / source identity to node session
 *   - Mark the canvas source node configured
 *   - Open the Data Profiling confirmation overlay
 */
export function useFinalizeRegisteredSourceMutation() {
  const { setSourceData } = useSourceData()

  return useMutation<FinalizeRegisteredSourceResult, Error, FinalizeRegisteredSourceParams>({
    mutationFn: async ({ sourceName }) => {
      const pipelineState = store.getState().pipeline
      if (pipelineState.isDataFlowCreated) {
        return {
          sessionId: pipelineState.sessionId,
          dataFlowId: pipelineState.dataFlowId,
        }
      }

      const dataFlowName = pipelineState.pipelineName.trim() || 'Untitled_Pipeline'
      const description = pipelineState.pipelineDescription?.trim() ?? ''
      const response = await pipelineService.createDataFlow({
        data_flow_name: dataFlowName,
        session_id: crypto.randomUUID(),
        description,
        purpose: '',
        status: '',
        web_socket_connection_id: '',
        data_flow_type: 'ETL',
        // Source is already registered — name-collision check would always fail.
        validate_source_name: false,
        source_names: sourceName ? [sourceName] : [],
      })

      return {
        sessionId: response.session_id,
        dataFlowId: response.data_flow_id,
      }
    },
    onSuccess: (result, params) => {
      const dispatch = store.dispatch

      dispatch(
        setPipelineState({
          isDataFlowCreated: true,
          sessionId: result.sessionId,
          dataFlowId: result.dataFlowId,
        }),
      )

      setSourceData(params.nodeId, {
        source_data_source_id: params.sourceDataSourceId ?? undefined,
        roo_id: params.rooId ?? undefined,
        source_name: params.sourceName ?? undefined,
        source_files: params.sourceFiles,
        source_selected_table: params.tableName,
      })

      mergeAdpSessionNode(params.nodeId, {
        sourceConfigurationState: {
          mode: 'new-source',
          isConfigured: true,
          isReadOnly: true,
          values: params.registeredSourceValues ?? {},
          schemaFields: [],
          postTestFields: [],
          source_name: params.sourceName ?? undefined,
          submitted_source_payload: params.submittedSourcePayload ?? undefined,
          tables: [params.tableName],
          sheetPaths: [],
          sheetNames: [],
        },
      })

      const snapshot = store.getState()
      const nextNodes = [...snapshot.canvas.present.nodes]
      const sourceNodeIndex = nextNodes.findIndex(
        (node) => node.id === params.nodeId && node.type === 'source',
      )
      if (sourceNodeIndex >= 0) {
        const sourceNode = nextNodes[sourceNodeIndex]
        nextNodes[sourceNodeIndex] = {
          ...sourceNode,
          data: {
            ...sourceNode.data,
            nodeSecondaryTitle: params.sourceName ?? sourceNode.data?.nodeSecondaryTitle,
            mode: 'existing-source',
            completed_steps: ['configuration-details'],
          },
        } as typeof sourceNode
        dispatch(
          syncCanvasState({
            nodes: nextNodes,
            edges: snapshot.canvas.present.edges,
          }),
        )
      }

      dispatch(
        openOverlay({
          type: 'data-profiling-confirmation',
          data: { nodeId: params.nodeId },
        }),
      )
    },
  })
}
